/**
 * PipelineStepBox Component
 *
 * Individual step box in the pipeline flow visualization.
 * Shows icon, label, count, and status color.
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Upload,
  Sparkles,
  Target,
  Search,
  Brain,
  FileText,
  Globe,
  BarChart3,
  Loader2
} from 'lucide-react';

// Step icon mapping
const stepIcons: Record<string, React.ElementType> = {
  imported: Upload,
  enriched: Sparkles,
  benefit_scored: Target,
  researched: Search,
  embeddings: Brain,
  aeo_generated: FileText,
  published: Globe,
  measured: BarChart3
};

// Status colors
const statusColors: Record<string, string> = {
  green: 'border-green-500 bg-green-50 dark:bg-green-950/30',
  yellow: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30',
  red: 'border-red-500 bg-red-50 dark:bg-red-950/30',
  blocked: 'border-gray-300 bg-gray-50 dark:bg-gray-800/50 opacity-60'
};

const iconColors: Record<string, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
  blocked: 'text-gray-400 dark:text-gray-500'
};

export interface PipelineStep {
  id: string;
  name: string;
  status: 'green' | 'yellow' | 'red' | 'blocked';
  count: number;
  previousStepCount: number | null;
  percentageFromPrevious: number | null;
  prerequisites: Array<{
    id: string;
    label: string;
    status: 'met' | 'partial' | 'missing';
    current?: number;
    recommended?: number;
    tooltip?: string;
  }>;
  metrics: {
    lastActivity: string | null;
    qualityIndicator?: {
      label: string;
      value: string;
      status: string;
    } | null;
  };
  action: {
    label: string;
    url: string | null;
    canProceed: boolean;
    inlineAction?: string;
  };
}

interface PipelineStepBoxProps {
  step: PipelineStep;
  onAction?: (step: PipelineStep) => void;
  isActionLoading?: boolean;
}

export function PipelineStepBox({ step, onAction, isActionLoading }: PipelineStepBoxProps) {
  const Icon = stepIcons[step.id] || FileText;
  const isBlocked = step.status === 'blocked';

  const handleClick = () => {
    if (!isBlocked && onAction) {
      onAction(step);
    }
  };

  return (
    <Card
      className={cn(
        'border-2 transition-all min-w-[140px]',
        statusColors[step.status],
        !isBlocked && 'hover:shadow-lg cursor-pointer'
      )}
      onClick={handleClick}
    >
      <CardContent className="pt-4 pb-3 px-3 flex flex-col items-center text-center">
        {/* Icon */}
        <div className={cn('mb-2', iconColors[step.status])}>
          <Icon className="h-8 w-8" />
        </div>

        {/* Step Name */}
        <p className="text-sm font-semibold mb-1 truncate w-full">
          {step.name}
        </p>

        {/* Count */}
        <p className={cn(
          'text-2xl font-bold',
          iconColors[step.status]
        )}>
          {step.count.toLocaleString()}
        </p>

        {/* Action Button */}
        {step.action.url || step.action.inlineAction ? (
          <Button
            size="sm"
            variant="outline"
            className="mt-2 w-full text-xs"
            disabled={!step.action.canProceed || isActionLoading}
            onClick={(e) => {
              e.stopPropagation();
              if (onAction) onAction(step);
            }}
          >
            {isActionLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              step.action.label
            )}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
